import * as React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  ButtonModel,
  IconSolidWarning,
  ModalContext,
  PictogramRegularInfoCheck,
  Text,
} from '@notino/react-styleguide';

import { useDrawer } from '@components/BottomSheet/DrawerProvider';
import { getScrollParent } from '@utils/dom';

import { ModalHeader } from '../ReviewsFormModalBody/components/ModalHeader';
import { messages as reviewFormMessages } from '../ReviewsFormModalBody/messages';

import { messages } from './messages';
import { ReviewableProductsSection } from './ReviewableProductsSection';
import {
  DoneButton,
  InfoLine,
  ReviewSentText,
  ReviewSentThanksText,
  ReviewsSentSuccesModalWrapper,
} from './styled';
import { useReviewableProducts } from './useReviewableProducts';
import { useReviewSubmitTracking } from './useReviewSubmitTracking';

type Props = {
  someImagesFailed?: boolean;
  showTryAgainButton?: boolean;
  onTryAgainClick?: () => void;
};

export const ReviewsSentSuccessModalBody = ({
  someImagesFailed,
  showTryAgainButton,
  onTryAgainClick,
}: Props) => {
  const { hideModal } = ModalContext.useModalContext();
  const { hideDrawer, isDrawerOpen, updateDrawerOptions } = useDrawer();
  const { products, loading } = useReviewableProducts();
  const { formatMessage } = useIntl();
  const wrapperRef = React.useRef<HTMLDivElement>(null);

  const { trackMyReviews, trackWriteReview, trackProductSelect } =
    useReviewSubmitTracking({
      variant: loading
        ? null
        : products.length > 0
        ? 'review_submit_products'
        : 'review_submit_blank',
    });

  // Coming from the form (which may be scrolled down), reset the modal/drawer
  // scroll position so the success state is shown from the top.
  React.useEffect(() => {
    getScrollParent(wrapperRef.current)?.scrollTo({ top: 0 });
  }, []);

  // On mobile the form runs inside a drawer with its own title header. The
  // success screen swaps it for the drawer's floating close button (which stays
  // pinned and gains a shadow on scroll), matching the desktop modal.
  React.useEffect(() => {
    if (isDrawerOpen) {
      updateDrawerOptions({
        header: undefined,
        floatingCloseButton: true,
        closeButtonAriaLabel: formatMessage(reviewFormMessages.closeModal),
      });
    }
  }, [isDrawerOpen, updateDrawerOptions, formatMessage]);

  const handleDone = () => {
    hideModal();
    hideDrawer();
  };

  return (
    <ReviewsSentSuccesModalWrapper
      ref={wrapperRef}
      data-cypress="reviewsModal-modalHeader"
    >
      <ModalHeader isSuccessModal={true} onClose={handleDone} />

      <div>
        <PictogramRegularInfoCheck height="72px" width="72px" />
        <ReviewSentText aria-live="polite">
          <FormattedMessage
            {...(someImagesFailed
              ? messages.reviewSentWithFailedImages
              : messages.reviewSent)}
          />
        </ReviewSentText>
        <ReviewSentThanksText>
          <FormattedMessage
            {...(someImagesFailed
              ? messages.reviewSendWithFailedImagesThanks
              : messages.reviewSentThanks)}
          />
        </ReviewSentThanksText>
      </div>

      {showTryAgainButton && (
        <InfoLine>
          <IconSolidWarning width="16px" height="16px" />
          <Text
            typography="labelSmall400"
            color="text.secondary"
            textAlign="start"
          >
            <FormattedMessage {...messages.infoLineSomeImagesFailed} />
          </Text>
          <button onClick={onTryAgainClick}>
            <FormattedMessage {...messages.tryAgain} />
          </button>
        </InfoLine>
      )}

      <ReviewableProductsSection
        products={products}
        onMyReviewsClick={trackMyReviews}
        onWriteReviewClick={trackWriteReview}
        onProductClick={trackProductSelect}
      />

      {products.length === 0 && (
        <DoneButton
          buttonStyle={ButtonModel.Styles.secondary}
          onClick={handleDone}
        >
          <FormattedMessage {...messages.done} />
        </DoneButton>
      )}
    </ReviewsSentSuccesModalWrapper>
  );
};
